import type { PublicUser } from "@scenix/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, onSessionEnded } from "./api";

export type Session =
  | { state: "loading" }
  /**
   * `ended` when a session was here and is not any more, so the sign-in screen
   * can say why it is being shown to somebody who did not ask for it
   * (ADR 000131). False on the ordinary arrival of somebody who is simply not
   * signed in, and said rather than left out, because which of the two this is
   * is a fact every one of these answers has.
   */
  | { state: "signed-out"; ended: boolean }
  | { state: "signed-in"; user: PublicUser };

interface SessionContextValue {
  session: Session;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  /**
   * How many times this browser has been told who is signed in.
   *
   * Counted where the answer arrives, so an ending can name the reading it
   * contradicts (below). A clock cannot do that job: the 401 and the read it
   * overturns are two responses, and two responses can share a millisecond.
   */
  const [reads, setReads] = useState(0);

  const { data, isPending, error } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const me = await api.currentUser();
      setReads((counted) => counted + 1);
      return me;
    },
    /**
     * A 401 is the answer, not a failure — it means "not signed in". Retrying
     * it would just delay rendering the sign-in screen.
     */
    retry: (failureCount, err) =>
      !(err instanceof ApiError && err.isUnauthenticated) && failureCount < 2,
    staleTime: 5 * 60_000,
  });

  /**
   * Which reading of the session a 401 has since overturned, if one has
   * (ADR 000131).
   *
   * Nothing here re-asks on its own: the query is fresh for five minutes and
   * refetching on focus is off, deliberately, because production work sits open
   * in a tab for hours. So a session that ends elsewhere has to be read from the
   * refusals that follow, and this is where they land.
   */
  const [endedAfter, setEndedAfter] = useState<number | null>(null);

  // Resubscribed on each reading, so the listener always names the current one.
  useEffect(() => onSessionEnded(() => setEndedAfter(reads)), [reads]);

  const signOut = useCallback(async () => {
    await api.signOut();
    // Drop every cached query: none of it belongs to whoever signs in next.
    queryClient.clear();
  }, [queryClient]);

  const session = useMemo<Session>(() => {
    // Checked before the user below, because the user is exactly what it
    // contradicts: whoever was signed in at that reading no longer is. A later
    // reading outranks it in turn, which is how signing in again gets back in.
    //
    // A session in hand is what makes this an ending worth reporting, rather
    // than the count being past zero: signing out drops the cache first, so
    // there is nobody to have been signed out from by the time its own 401
    // arrives, and a person who pressed Sign out does not need telling.
    if (endedAfter === reads) return { state: "signed-out", ended: data !== undefined };
    if (data) return { state: "signed-in", user: data };
    if (isPending) return { state: "loading" };
    if (error instanceof ApiError && error.isUnauthenticated) {
      return { state: "signed-out", ended: false };
    }
    // Any other failure (API down, network gone) still means no usable session.
    return error ? { state: "signed-out", ended: false } : { state: "loading" };
  }, [data, isPending, error, endedAfter, reads]);

  const value = useMemo(() => ({ session, signOut }), [session, signOut]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionContextValue {
  const value = use(SessionContext);
  if (!value) throw new Error("useSession must be used within a SessionProvider");
  return value;
}
